import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { financingAPI } from '../../services/api';

const INSTITUTION_LABELS = {
  banque: 'Banque',
  microfinance: 'Institution de microfinance',
  fonds_garantie: 'Fonds de garantie',
  investisseur: 'Investisseur / Capital-risque',
  ong: 'ONG',
  etat: 'État / Programme public',
  autre: 'Autre',
};

const FINANCING_TYPE_LABELS = {
  credit: 'Crédit / Prêt',
  subvention: 'Subvention / Don',
  investissement: 'Investissement en capital',
  garantie: 'Garantie',
  autre: 'Autre',
};

const MATURITY_LABELS = {
  idee: 'Idée',
  prototype: 'Prototype',
  creation: 'Création',
  amorcage: 'Amorçage',
  croissance: 'Croissance',
  developpement: 'Développement',
  expansion: 'Expansion / International',
};

const FinancingDetail = () => {
  const { id } = useParams();

  const { data: product, isLoading, error } = useQuery(['financing-product', id], async () => {
    const res = await financingAPI.getProduct(id);
    return res.data;
  });

  const formatAmount = (value, currency) => {
    if (value === null || value === undefined) return null;
    return `${Number(value).toLocaleString('fr-FR')} ${currency || ''}`.trim();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background py-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-background py-12 text-center">
        <h2 className="text-2xl font-semibold text-text mb-4">Produit introuvable</h2>
        <Link to="/financing" className="text-primary-600 hover:text-primary-800 font-medium">
          Retour au catalogue de financement
        </Link>
      </div>
    );
  }

  const amountMin = formatAmount(product.amount_min, product.currency);
  const amountMax = formatAmount(product.amount_max, product.currency);

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/financing" className="text-primary-600 hover:text-primary-800 text-sm font-medium">
          ← Retour au catalogue
        </Link>

        <div className="bg-white rounded-xl shadow-lg p-8 mt-4">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="px-2 py-1 bg-primary-100 text-primary-800 text-xs rounded-full">
              {FINANCING_TYPE_LABELS[product.financing_type] || product.financing_type}
            </span>
            <span className="px-2 py-1 bg-secondary-100 text-secondary-800 text-xs rounded-full">
              {INSTITUTION_LABELS[product.institution_type] || product.institution_type}
            </span>
          </div>

          <h1 className="text-3xl font-bold text-text mb-2">{product.name}</h1>
          <p className="text-lg text-gray-600 mb-6">{product.institution_name}</p>

          <p className="text-gray-700 whitespace-pre-line mb-6">{product.description}</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            {(amountMin || amountMax) && (
              <div>
                <h4 className="text-sm font-semibold text-gray-500 mb-1">Montant du financement</h4>
                <p className="text-text">
                  {amountMin && amountMax ? `${amountMin} - ${amountMax}` : (amountMin || amountMax)}
                </p>
              </div>
            )}
            {product.duration_months !== null && product.duration_months !== undefined && (
              <div>
                <h4 className="text-sm font-semibold text-gray-500 mb-1">Durée</h4>
                <p className="text-text">{product.duration_months} mois</p>
              </div>
            )}
            {product.interest_rate && (
              <div>
                <h4 className="text-sm font-semibold text-gray-500 mb-1">Taux d'intérêt</h4>
                <p className="text-text">{product.interest_rate}</p>
              </div>
            )}
          </div>

          {Array.isArray(product.maturity_levels) && product.maturity_levels.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-500 mb-2">Niveaux de maturité ciblés</h4>
              <div className="flex flex-wrap gap-2">
                {product.maturity_levels.map((m) => (
                  <span key={m} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                    {MATURITY_LABELS[m] || m}
                  </span>
                ))}
              </div>
            </div>
          )}

          {Array.isArray(product.sectors) && product.sectors.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-500 mb-2">Secteurs concernés</h4>
              <div className="flex flex-wrap gap-2">
                {product.sectors.map((s) => (
                  <span key={s} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}

          {product.eligibility && (
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-500 mb-2">Critères d'éligibilité</h4>
              <p className="text-gray-700 whitespace-pre-line">{product.eligibility}</p>
            </div>
          )}

          <div className="border-t border-gray-200 pt-6 space-y-2">
            {product.application_url && (
              <a
                href={product.application_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
              >
                Faire une demande
              </a>
            )}
            <div className="text-sm text-gray-500 mt-3 space-y-1">
              {product.contact_email && <p>Email : {product.contact_email}</p>}
              {product.contact_phone && <p>Téléphone : {product.contact_phone}</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancingDetail;
